import {
  FC,
  createContext,
  useMemo,
  useContext,
  useState,
  useEffect,
} from "react";
import { useNavigate } from "react-router";

import { useAuth } from "../../auth";
import { createBearAuthedHeader } from "../api";
import { requestForm } from "../services";
import {
  PostData,
  Action,
  Model,
  VviinnFormsProviderProps,
  Notification,
} from "../types";
import { useNotifications } from "./useFormNotification";
import { NotificationType } from "../components/Notifications";
import { TypedBooleanPromise } from "../../helpers/functions/types";

type ModelVoid = Model | void;

type FormContextT = {
  handleFieldChange: (
    name: string | undefined,
    value: string | boolean | number | string[],
  ) => void;
  handleFormAccept: TypedBooleanPromise<Action | undefined>;
  postData: PostData;
  model: ModelVoid;
};

type FormRequestData = {
  id?: string;
  contract_id?: string;
  company_id?: string;
  action_id?: string;
};

const FormContext = createContext<FormContextT>({
  handleFieldChange: () => {},
  handleFormAccept: () => Promise.resolve(true),
  postData: {},
  model: {},
});

export const FormProvider: FC<
  React.PropsWithChildren<VviinnFormsProviderProps>
> = ({
  token,
  formType,
  children,
  id,
  contractId,
  companyId,
  select,
  filters,
  readonlyColumns,
  requestType,
  actionId,
  onSuccess,
}) => {
  const [postData, setPostData] = useState<PostData>({});
  const [model, setModel] = useState<Model | void>();
  const [bearerString, setBearerString] = useState<string>();
  const { notify } = useNotifications();
  const navigate = useNavigate();

  const formRequestData: FormRequestData = {};
  if (id) formRequestData.id = id;
  if (contractId) formRequestData.contract_id = contractId;
  if (companyId) formRequestData.company_id = companyId;

  const { getToken } = useAuth();

  const pushNotification = (notification?: Notification): void => {
    if (!notification) {
      return;
    }
    let type: NotificationType = "Info";
    switch (notification?.type) {
      case "information":
        type = "Success";
        break;
      case "error":
        type = "Error";
        break;
      case "warning":
        type = "Warning";
        break;
    }
    notify(type, notification.message);
  };

  const redirectAfterShowNotification = (redirectUrl: string) =>
    setTimeout(() => {
      navigate(redirectUrl);
      return;
    }, 1000);

  useEffect(() => {
    (async () => {
      try {
        //TODO remove appToken logic when make the component independent
        const appToken = await getToken();
        const bearerString = createBearAuthedHeader(token ?? appToken);
        setBearerString(bearerString);
      } catch (e) {}
    })();
  }, [getToken]);

  useEffect(() => {
    (async () => {
      if (bearerString) {
        requestForm(
          formType,
          bearerString,
          formRequestData,
          requestType ?? (id === "" ? "POST" : "GET"),
          select,
          readonlyColumns,
          undefined,
          undefined,
          filters,
          actionId,
        ).then((data: ModelVoid) => {
          setModel(data);
          pushNotification(data?.notification);
          const redirectUrl = data?.metadata?.redirect_url;
          if (redirectUrl) {
            redirectAfterShowNotification(redirectUrl);
          }
        });
      }
    })();
  }, [formType, bearerString, companyId]);

  const value = useMemo(() => {
    const handleFieldChange = (
      name: string | undefined,
      value: string | boolean | number | string[],
    ) => {
      if (name) {
        postData[name] = value;
        setPostData({ ...postData });
      }
    };

    const handleFormAccept = async (action?: Action) => {
      const formPutData = { ...action?.parameters?.form, ...postData };

      // formPutData starts from the full previous field snapshot, so switching
      // authentication_mode away from single_sign_on (without also re-entering every SSO
      // field) still leaves the stale authentication_provider/sso_provider_id/sso_domains
      // values in there — the bff rejects the request unless those are absent entirely
      // for a plain email/password company.
      if (
        formType === "company_authentication_config" &&
        formPutData.authentication_mode !== "single_sign_on"
      ) {
        delete formPutData.authentication_provider;
        delete formPutData.sso_provider_id;
        delete formPutData.sso_domains;
      }

      notify("Info", `Updating ${model?.item?.name ?? "field(s)"}`);

      if (bearerString) {
        const data = await requestForm(
          formType,
          bearerString,
          formPutData,
          action?.request_type,
          action?.select,
          action?.readonly_columns,
          action?.label,
          action?.navigate_url,
          undefined,
          action?.id,
        );
        const dataNotification = data?.notification;
        const redirectUrl = data?.metadata?.redirect_url;
        const dataItem = data?.item;

        if (!(dataItem || dataNotification)) {
          notify(
            "Error",
            `Could not update ${model?.item?.name ?? "field(s)"}`,
          );
          return false;
        } else {
          if (dataItem) {
            setModel(data);
          }
          if (
            onSuccess &&
            (dataItem || dataNotification?.type === "information")
          ) {
            onSuccess();
          } else
            pushNotification(
              dataNotification ?? {
                type: "information",
                message: `${model?.item?.name ?? "field(s)"} updated`,
              },
            );
        }
        setPostData({});
        if (redirectUrl) {
          redirectAfterShowNotification(redirectUrl);
        }
        return true;
      }
      return false;
    };

    return {
      handleFieldChange,
      handleFormAccept,
      postData,
      model,
    };
  }, [postData, model, notify]);

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};

export const useForm = () => useContext(FormContext);
